<script lang="ts">
  import { Badge, Button, FormInput, MultiSelect } from '../common';
  import ConfirmDialog from '../common/ConfirmDialog.svelte';
  import { createConfirmDialog } from '../../lib/confirmDialog.svelte';
  import BudgetProgressBar from './BudgetProgressBar.svelte';
  import { getBudgetBadgeVariant, getBudgetStatusText } from '../../lib/budgetUtils';
  import Edit from '../icons/Edit.svelte';
  import Trash from '../icons/Trash.svelte';

  const confirmDialog = createConfirmDialog();

  type BudgetStatus = {
    name: string;
    categories: string[];
    type: 'monthly' | 'quarterly' | 'yearly';
    limit: number;
    spent: number;
    remaining: number;
    percentage: number;
    allowRollover?: boolean;
    resetRolloverYearly?: boolean;
    isFixedExpense?: boolean;
    rolloverAmount?: number;
    totalLimit?: number;
    isUnbudgeted?: boolean;
    // Legacy support for unbudgeted items
    category?: string;
  };

  type Props = {
    status: BudgetStatus;
    period: string; // e.g., "2024-06" for June 2024, or "2024" for yearly view
    density?: 'compact' | 'comfortable' | 'spacious';
    availableCategories?: string[];
    onEdit: (name: string, categories: string[], type: 'monthly' | 'quarterly' | 'yearly', newLimit: number, allowRollover: boolean, isFixedExpense: boolean, resetRolloverYearly: boolean) => Promise<void>;
    onDelete: (name: string, type: 'monthly' | 'quarterly' | 'yearly') => Promise<void>;
    onConvertType?: (name: string, categories: string[], fromType: 'monthly' | 'quarterly' | 'yearly', toType: 'monthly' | 'quarterly' | 'yearly', limit: number, allowRollover: boolean, isFixedExpense: boolean, resetRolloverYearly: boolean) => Promise<void>;
    onResetRollover?: (name: string, type: 'monthly' | 'quarterly' | 'yearly') => Promise<void>;
  };

  let { status, period, density = 'comfortable', availableCategories = [], onEdit, onDelete, onConvertType, onResetRollover }: Props = $props();

  // Convert period string to Date for utility functions
  const periodDate = $derived(() => {
    const now = new Date();

    if (period.length === 4) {
      // Yearly view: "2024"
      const periodYear = parseInt(period);
      // If viewing current year, use today's date; otherwise use January 1
      if (periodYear === now.getFullYear()) {
        return now;
      }
      return new Date(periodYear, 0, 1);
    } else {
      // Monthly view: "2024-06"
      const [year, month] = period.split('-');
      const periodYear = parseInt(year);
      const periodMonth = parseInt(month) - 1;

      // If viewing current month, use today's date; otherwise use first of month
      if (periodYear === now.getFullYear() && periodMonth === now.getMonth()) {
        return now;
      }
      return new Date(periodYear, periodMonth, 1);
    }
  });

  let isEditing = $state(false);
  let editName = $state('');
  let editValue = $state('');
  let editType = $state<'monthly' | 'quarterly' | 'yearly'>('monthly');
  let editCategories = $state<string[]>([]);
  let editAllowRollover = $state(false);
  let editResetRolloverYearly = $state(false);
  let editIsFixedExpense = $state(false);
  let isSubmitting = $state(false);

  const handleEdit = () => {
    isEditing = true;
    editName = status.name;
    editValue = status.limit.toString();
    editType = status.type;
    editCategories = [...status.categories];
    editAllowRollover = status.allowRollover || false;
    editResetRolloverYearly = status.resetRolloverYearly || false;
    editIsFixedExpense = status.isFixedExpense || false;
  };

  const handleCancel = () => {
    isEditing = false;
    editName = status.name;
    editValue = status.limit.toString();
    editType = status.type;
    editCategories = [...status.categories];
    editAllowRollover = status.allowRollover || false;
    editResetRolloverYearly = status.resetRolloverYearly || false;
    editIsFixedExpense = status.isFixedExpense || false;
  };

  const handleSave = async () => {
    const newLimit = parseFloat(editValue);
    if (isNaN(newLimit) || newLimit < 0) {
      return;
    }

    if (editCategories.length === 0) {
      alert('Please select at least one category');
      return;
    }

    isSubmitting = true;
    try {
      // If type changed, we need to delete old and create new
      if (editType !== status.type && onConvertType) {
        await onConvertType(status.name, editCategories, status.type, editType, newLimit, editAllowRollover, editIsFixedExpense, editResetRolloverYearly);
      } else {
        await onEdit(status.name, editCategories, status.type, newLimit, editAllowRollover, editIsFixedExpense, editResetRolloverYearly);
      }
      isEditing = false;
    } catch (error) {
      console.error('Failed to update budget:', error);
    } finally {
      isSubmitting = false;
    }
  };

  const handleResetRollover = async () => {
    const confirmed = await confirmDialog.confirm({
      title: 'Reset Rollover',
      message: `Reset rollover amount for ${status.name}?`,
      confirmText: 'Reset',
      variant: 'warning'
    });

    if (!confirmed) return;

    isSubmitting = true;
    try {
      if (onResetRollover) {
        await onResetRollover(status.name, status.type);
      }
    } catch (error) {
      console.error('Failed to reset rollover:', error);
    } finally {
      isSubmitting = false;
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirmDialog.confirm({
      title: 'Delete Budget',
      message: `Are you sure you want to delete the ${status.type} budget "${status.name}"?`,
      confirmText: 'Delete',
      variant: 'danger'
    });

    if (!confirmed) return;

    isSubmitting = true;
    try {
      await onDelete(status.name, status.type);
    } catch (error) {
      console.error('Failed to delete budget:', error);
    } finally {
      isSubmitting = false;
    }
  };

  const isUnbudgeted = $derived(status.isUnbudgeted || status.type === 'unbudgeted');
</script>

<div class="budget-card" class:unbudgeted={isUnbudgeted} class:compact={density === 'compact'} class:spacious={density === 'spacious'}>
  {#if !isUnbudgeted && !isEditing}
    <div class="budget-actions">
      <button
        class="icon-button"
        onclick={handleEdit}
        disabled={isSubmitting}
        aria-label="Edit budget"
        title="Edit"
      >
        <Edit size={16} />
      </button>
      <button
        class="icon-button"
        onclick={handleDelete}
        disabled={isSubmitting}
        aria-label="Delete budget"
        title="Delete"
      >
        <Trash size={16} />
      </button>
    </div>
  {/if}

  <div class="budget-header">
    <div class="budget-title-row">
      <h3 class="budget-name">
        {status.name || status.category}{#if !isUnbudgeted && status.categories && (status.categories.length > 1 || (status.categories.length === 1 && status.categories[0] !== status.name))}:<span class="categories-inline">{status.categories.join(', ')}</span>{/if}
      </h3>
    </div>
    <div class="budget-info-container">
      {#if !isUnbudgeted}
        <div class="budget-badges">
          <Badge variant={getBudgetBadgeVariant(status.percentage, status.type, periodDate(), status.isFixedExpense)} size="xs">
            {getBudgetStatusText(status.percentage, status.type, periodDate(), status.isFixedExpense)}
          </Badge>
          {#if status.isFixedExpense}
            <Badge variant="info" size="xs">
              Fixed
            </Badge>
          {/if}
          {#if status.allowRollover}
            <Badge variant="success" size="xs">
              Rollover{#if status.resetRolloverYearly} (Yearly){/if}
            </Badge>
          {/if}
        </div>
      {:else}
        <Badge variant="info" size="xs">
          Unbudgeted
        </Badge>
      {/if}
    </div>
  </div>

  {#if isEditing}
    <div class="budget-edit">
      <!-- Top row: Name - Categories - Limit -->
      <div class="edit-row-top">
        <div class="edit-field-inline">
          <FormInput
            bind:value={editName}
            type="text"
            placeholder="Budget Name"
            label="Name"
            disabled={isSubmitting}
          />
        </div>

        <div class="edit-field-inline">
          <MultiSelect
            bind:value={editCategories}
            options={availableCategories}
            label="Categories"
            placeholder="Select categories..."
            disabled={isSubmitting}
            maxHeight="250px"
          />
        </div>

        <div class="edit-field-inline">
          <FormInput
            bind:value={editValue}
            type="number"
            placeholder="Amount"
            label="Limit"
            disabled={isSubmitting}
          />
        </div>
      </div>

      <!-- Bottom row: Period - Options - Actions -->
      <div class="edit-row-bottom">
        <div class="type-buttons-compact">
          <button
            type="button"
            class="type-btn-compact"
            class:active={editType === 'monthly'}
            onclick={() => editType = 'monthly'}
            disabled={isSubmitting}
          >
            Monthly
          </button>
          <button
            type="button"
            class="type-btn-compact"
            class:active={editType === 'quarterly'}
            onclick={() => editType = 'quarterly'}
            disabled={isSubmitting}
          >
            Quarterly
          </button>
          <button
            type="button"
            class="type-btn-compact"
            class:active={editType === 'yearly'}
            onclick={() => editType = 'yearly'}
            disabled={isSubmitting}
          >
            Yearly
          </button>
        </div>

        <div class="options-group">
          <label class="option-checkbox">
            <input type="checkbox" bind:checked={editIsFixedExpense} disabled={isSubmitting} />
            <span>Fixed</span>
          </label>
          <label class="option-checkbox">
            <input type="checkbox" bind:checked={editAllowRollover} disabled={isSubmitting} />
            <span>Rollover</span>
          </label>
          <label class="option-checkbox" class:invisible={!editAllowRollover}>
            <input type="checkbox" bind:checked={editResetRolloverYearly} disabled={isSubmitting || !editAllowRollover} />
            <span>Reset Yearly</span>
          </label>
        </div>

        <div class="edit-actions">
          <Button variant="primary" size="sm" onclick={handleSave} disabled={isSubmitting}>
            Save
          </Button>
          <Button variant="ghost" size="sm" onclick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  {:else}
    {#if !isUnbudgeted}
      <div class="budget-progress">
        <BudgetProgressBar
          percentage={status.percentage}
          spent={status.spent}
          limit={status.limit}
          rolloverAmount={status.rolloverAmount || 0}
          totalLimit={status.totalLimit}
          isFixedExpense={status.isFixedExpense || false}
          budgetType={status.type}
          periodDate={periodDate()}
        />
      </div>
    {:else}
      <!-- Unbudgeted: just show total spending -->
      <div class="unbudgeted-summary">
        <span class="unbudgeted-label">Total Spending:</span>
        <span class="unbudgeted-amount">${status.spent.toFixed(2)}</span>
      </div>
    {/if}
  {/if}
</div>

<ConfirmDialog {...confirmDialog.props} />

<style>
  .budget-card {
    position: relative;
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    transition: all var(--transition-base);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-xl);
  }

  /* Density Variants */
  .budget-card.compact {
    padding: var(--spacing-sm);
    border-radius: var(--radius-md);
  }

  .budget-card.spacious {
    padding: var(--spacing-2xl);
    border-radius: var(--radius-xl);
  }

  .budget-header {
    flex: 0 0 300px;
    margin-bottom: 0;
  }

  .budget-progress {
    flex: 1;
    margin-bottom: 0;
  }

  .budget-card:hover {
    box-shadow: var(--shadow-md);
  }

  .budget-card.unbudgeted {
    background: var(--color-background-tertiary);
    border-style: dashed;
    border-color: var(--color-border);
  }

  .budget-card.unbudgeted:hover {
    box-shadow: var(--shadow-none);
  }

  .budget-card .budget-header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    padding-right: 80px; /* Space for icon buttons */
  }

  .compact .budget-header {
    gap: var(--spacing-xs);
  }

  .spacious .budget-header {
    gap: var(--spacing-md);
  }

  .budget-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .budget-info-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .compact .budget-info-container {
    gap: var(--spacing-xs);
  }

  .spacious .budget-info-container {
    gap: var(--spacing-md);
  }

  .budget-badges {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
  }

  .compact .budget-badges {
    gap: var(--spacing-xs);
  }

  .budget-name {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0;
    white-space: nowrap;
    overflow: visible;
  }

  .compact .budget-name {
    font-size: var(--font-size-base);
  }

  .spacious .budget-name {
    font-size: var(--font-size-xl);
  }

  .categories-inline {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-normal);
    color: var(--color-text-tertiary);
    margin-left: var(--spacing-xs);
  }

  .budget-actions {
    position: absolute;
    top: var(--spacing-md);
    right: var(--spacing-md);
    display: flex;
    gap: var(--spacing-xs);
    z-index: 10;
  }

  .compact .budget-actions {
    top: var(--spacing-xs);
    right: var(--spacing-xs);
  }

  .spacious .budget-actions {
    top: var(--spacing-lg);
    right: var(--spacing-lg);
  }

  .icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-md);
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  .icon-button:hover {
    background: var(--color-background-secondary);
    color: var(--color-text-primary);
  }

  .icon-button:active {
    transform: scale(0.95);
  }

  .icon-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .budget-edit {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    padding-top: var(--spacing-md);
    border-top: 1px solid var(--color-border);
  }

  .edit-row-top {
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;
    gap: var(--spacing-md);
    align-items: end;
  }

  .edit-row-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-md);
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--color-background-secondary);
    border-radius: var(--radius-md);
  }

  .edit-field-inline {
    display: flex;
    flex-direction: column;
  }

  .type-buttons-compact {
    display: flex;
    gap: var(--spacing-xs);
    background: var(--color-background);
    padding: var(--spacing-xs);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    flex-shrink: 0;
  }

  .type-btn-compact {
    padding: var(--spacing-xs) var(--spacing-sm);
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: all var(--transition-fast);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
  }

  .type-btn-compact:hover:not(:disabled) {
    background: var(--color-primary-light);
    color: var(--color-text-primary);
  }

  .type-btn-compact.active {
    background: var(--color-primary);
    color: white;
  }

  .type-btn-compact:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .options-group {
    display: flex;
    gap: var(--spacing-sm);
    flex: 1;
    min-width: 0;
  }

  .option-checkbox {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    cursor: pointer;
    user-select: none;
    flex-shrink: 0;
  }

  .option-checkbox.invisible {
    visibility: hidden;
    pointer-events: none;
  }

  .option-checkbox input[type="checkbox"] {
    cursor: pointer;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .option-checkbox span {
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    white-space: nowrap;
  }

  .edit-actions {
    display: flex;
    gap: var(--spacing-sm);
    margin-left: auto;
  }

  .budget-card .budget-progress {
    flex: 1;
    margin-bottom: 0;
  }

  .unbudgeted-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-md) 0;
  }

  .unbudgeted-label {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    font-weight: var(--font-weight-medium);
  }

  .unbudgeted-amount {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
  }

  @media (max-width: 768px) {
    .budget-card {
      flex-direction: column;
      align-items: stretch;
    }

    .budget-header {
      flex: auto;
      padding-right: 80px;
    }

    .budget-progress {
      flex: auto;
    }

    .budget-card .budget-header {
      margin-bottom: var(--spacing-lg);
    }

    .compact .budget-header {
      margin-bottom: var(--spacing-sm);
    }

    .spacious .budget-header {
      margin-bottom: var(--spacing-2xl);
    }

    .edit-row-top {
      grid-template-columns: 1fr;
    }

    .edit-row-bottom {
      flex-direction: column;
      align-items: stretch;
    }

    .edit-actions {
      margin-left: 0;
      width: 100%;
    }

    .edit-actions :global(button) {
      flex: 1;
    }
  }
</style>
